import { useEffect, useRef, useState } from 'react';
import { Skeleton } from '@mui/material';

import { DropdownOption, MultiSelectDropdownProps } from 'types/dropdown';
import useOnClickOutside from '@hooks/clickOutside';

import Icon from '../icon/Icon';
import SearchBar from '../searchbar/SearchBar';
import Checkbox from '../checkbox/Checkbox';
import PlusNChipList from '../plus-n-chip-list/PlusNChipList';
import MultiValueDropDownLabel from '../multi-value-drop-label/MultiValueDropDownLabel';

const MultiSelectDropdown = ({
  label,
  searchBarPlaceholder,
  options,
  selectedOptions,
  onChange,
  className,
  displayChipsCount = 2,
  plusMoreClickRequired = true,
  isLoading,
  isMultiLabeled = false,
  dropdownHeight = 'h-200',
  isDropdownOpenByDefault = false,
  embedded = false
}: MultiSelectDropdownProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(
    isDropdownOpenByDefault
  );

  const [searchValue, setSearchValue] = useState<string>('');

  /**
   * Find selected option index.
   */
  const findIndex = (item: DropdownOption) =>
    selectedOptions.findIndex((option) => option.id === item.id);

  /**
   * Filter options based on search.
   */
  const filteredOptions: DropdownOption[] = searchValue
    ? options.filter((option: DropdownOption) =>
        option.label?.toLowerCase().includes(searchValue.toLowerCase())
      )
    : options;

  /**
   * Handle selecting / unselecting an option.
   */
  const handleOnChange = (
    element: DropdownOption,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checkedState = event.target.checked;
    const index = findIndex(element);

    if (checkedState) {
      if (index < 0) {
        onChange([...selectedOptions, element]);
      }

      return;
    }

    if (index >= 0) {
      onChange(selectedOptions.filter((option) => option.id !== element.id));
    }
  };

  /**
   * Remove selected chip.
   */
  const handleOnRemove = (item: DropdownOption) => {
    onChange(selectedOptions.filter((option) => option.id !== item.id));
  };

  /**
   * Toggle normal dropdown.
   *
   * Embedded mode is always open and therefore
   * should not toggle.
   */
  const handleDropdownClick = () => {
    if (embedded) {
      return;
    }

    setIsDropdownOpen((previous) => !previous);
    setSearchValue('');
  };

  /**
   * Search.
   */
  const handleSearchBarChange = (searchTerm: string) => {
    setSearchValue(searchTerm);
  };

  /**
   * Close normal dropdown when clicking outside.
   *
   * Embedded mode doesn't need this behaviour.
   */
  useOnClickOutside(ref, () => {
    if (embedded) {
      return;
    }

    setIsDropdownOpen(false);
    setSearchValue('');
  });

  /**
   * Open dropdown by default when requested.
   */
  useEffect(() => {
    if (isDropdownOpenByDefault) {
      setIsDropdownOpen(true);
    }
  }, [isDropdownOpenByDefault]);

  /**
   * Loading state.
   */
  if (isLoading) {
    return (
      <div className="w-full">
        <Skeleton height={53} animation="wave" variant="rounded" />
      </div>
    );
  }

  /**
   * Selected values / label.
   */
  const renderDropdownLabel = () => {
    const onClickPlusMoreText = () => {
      if (!embedded) {
        setIsDropdownOpen(false);
      }
    };

    return (
      <>
        <span className="text-base text-grey40">
          {!selectedOptions.length && label}
        </span>

        <PlusNChipList
          selectedOptions={selectedOptions}
          handleOnRemove={handleOnRemove}
          plusMoreClickRequired={plusMoreClickRequired}
          displayChipsCount={displayChipsCount}
          chipClassName="bg-primary text-theme"
          tooltipChipClassName="bg-primary text-theme"
          onClickPlusMoreText={onClickPlusMoreText}
        />
      </>
    );
  };

  /**
   * Option label.
   */
  const renderOptionLabel = (item: DropdownOption) => {
    if (isMultiLabeled && item?.multiLabels) {
      return (
        <MultiValueDropDownLabel
          label={item.multiLabels.label}
          subLabel={item.multiLabels.subLabel}
        />
      );
    }

    return item.label;
  };

  /**
   * Options list.
   *
   * IMPORTANT:
   *
   * This is the ONLY scrollable area in embedded mode.
   */
  const renderOptions = () => {
    if (!filteredOptions.length) {
      return (
        <div className="flex min-h-0 flex-1 items-center justify-center text-base font-normal italic text-content">
          No results found
        </div>
      );
    }

    return (
      <div className="custom-scroll min-h-0 flex-1 overflow-y-auto">
        <div className="pb-2">
          {filteredOptions.map((item, key) => (
            <Checkbox
              key={item.id ?? item.value}
              label={renderOptionLabel(item)}
              handleChange={(event) => handleOnChange(item, event)}
              checked={findIndex(item) >= 0}
              className="py-2"
              labelStyle="!text-content text-base font-normal"
              htmlKey={String(key)}
            />
          ))}
        </div>
      </div>
    );
  };

  /**
   * ---------------------------------------------------------
   * EMBEDDED MODE
   * ---------------------------------------------------------
   *
   * Used inside the Select Topic and Skill modal.
   *
   * Layout:
   *
   * component
   *   ├── selected values
   *   └── search/results panel
   *         ├── search
   *         └── scrollable results
   */
  if (embedded) {
    return (
      <div
        ref={ref}
        className={`flex h-full min-h-0 w-full flex-col ${className || ''}`}
      >
        {/* Selected values */}
        <div
          className="flex min-h-[52px] shrink-0 items-center rounded-xl border border-grey40 bg-white px-4 py-2"
          role="presentation"
        >
          {renderDropdownLabel()}
        </div>

        {/* Search + results */}
        <div className="mt-2 flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-grey40 bg-white p-4 shadow-sm">
          {/* Search */}
          <div className="shrink-0">
            <SearchBar
              placeholder={searchBarPlaceholder}
              onSearch={handleSearchBarChange}
              value={searchValue}
              searchInputStyle="pr-50"
            />
          </div>

          {/* Results */}
          <div className="mt-3 flex min-h-0 flex-1 flex-col overflow-hidden">
            {renderOptions()}
          </div>
        </div>
      </div>
    );
  }

  /**
   * ---------------------------------------------------------
   * NORMAL DROPDOWN MODE
   * ---------------------------------------------------------
   *
   * Existing behaviour for other screens.
   */
  return (
    <div ref={ref} className={`relative w-full ${className || ''}`}>
      {/* Dropdown trigger */}
      <div
        role="presentation"
        onClick={handleDropdownClick}
        className="relative flex min-h-[52px] cursor-pointer items-center rounded-xl border border-grey40 p-2 hover:border-primary desktop:px-4 desktop:py-2"
      >
        {renderDropdownLabel()}

        <Icon
          name="up"
          className={`absolute right-4 ${isDropdownOpen ? '' : 'rotate-180'}`}
        />
      </div>

      {/* Dropdown */}
      {isDropdownOpen && (
        <div
          className={`${dropdownHeight} absolute right-0 z-10 mt-1 flex w-full flex-col overflow-hidden rounded-xl border border-grey40 bg-white p-4 shadow-sm`}
        >
          {/* Search */}
          <div className="shrink-0">
            <SearchBar
              placeholder={searchBarPlaceholder}
              onSearch={handleSearchBarChange}
              value={searchValue}
              searchInputStyle="pr-50"
            />
          </div>

          {/* Results */}
          <div className="mt-3 flex min-h-0 flex-1 flex-col overflow-hidden">
            {renderOptions()}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
